// File-type checks, size limits and output names shared by every tool.
// Pure functions: no React, no DOM beyond the File/Blob types.

export type FileKind =
  | "pdf"
  | "jpg"
  | "png"
  | "webp"
  | "avif"
  | "heic"
  | "gif"
  | "bmp"
  // Office documents (Office → PDF, a server tool)
  | "doc"
  | "docx"
  | "xls"
  | "xlsx"
  | "ppt"
  | "pptx"
  | "odt"
  | "ods"
  | "odp";

const KINDS: Record<
  FileKind,
  { label: string; mimes: string[]; exts: string[] }
> = {
  pdf: { label: "PDF", mimes: ["application/pdf"], exts: ["pdf"] },
  jpg: { label: "JPG", mimes: ["image/jpeg"], exts: ["jpg", "jpeg"] },
  png: { label: "PNG", mimes: ["image/png"], exts: ["png"] },
  webp: { label: "WebP", mimes: ["image/webp"], exts: ["webp"] },
  avif: { label: "AVIF", mimes: ["image/avif"], exts: ["avif"] },
  heic: {
    label: "HEIC",
    mimes: ["image/heic", "image/heif"],
    exts: ["heic", "heif"],
  },
  gif: { label: "GIF", mimes: ["image/gif"], exts: ["gif"] },
  bmp: { label: "BMP", mimes: ["image/bmp"], exts: ["bmp"] },
  doc: { label: "DOC", mimes: ["application/msword"], exts: ["doc"] },
  docx: {
    label: "DOCX",
    mimes: [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
    exts: ["docx"],
  },
  xls: { label: "XLS", mimes: ["application/vnd.ms-excel"], exts: ["xls"] },
  xlsx: {
    label: "XLSX",
    mimes: [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
    exts: ["xlsx"],
  },
  ppt: {
    label: "PPT",
    mimes: ["application/vnd.ms-powerpoint"],
    exts: ["ppt"],
  },
  pptx: {
    label: "PPTX",
    mimes: [
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ],
    exts: ["pptx"],
  },
  odt: {
    label: "ODT",
    mimes: ["application/vnd.oasis.opendocument.text"],
    exts: ["odt"],
  },
  ods: {
    label: "ODS",
    mimes: ["application/vnd.oasis.opendocument.spreadsheet"],
    exts: ["ods"],
  },
  odp: {
    label: "ODP",
    mimes: ["application/vnd.oasis.opendocument.presentation"],
    exts: ["odp"],
  },
};

export const IMAGE_KINDS: FileKind[] = [
  "jpg",
  "png",
  "webp",
  "avif",
  "heic",
  "gif",
  "bmp",
];
export const OFFICE_KINDS: FileKind[] = [
  "docx",
  "doc",
  "xlsx",
  "xls",
  "pptx",
  "ppt",
  "odt",
  "ods",
  "odp",
];
export const ALL_KINDS: FileKind[] = ["pdf", ...IMAGE_KINDS, ...OFFICE_KINDS];

export const MB = 1024 * 1024;
export const MAX_FILE_BYTES = 100 * MB;
/** Server tools (Phase 5) take smaller files: Lambda streams at most ~20 MB back. */
export const SERVER_MAX_BYTES = 20 * MB;
export const MAX_BATCH_FILES = 50;
export const PHONE_WARN_BYTES = 25 * MB;

function extension(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
}

/** Which kind a file is, by MIME type first and then by extension
 *  (HEIC files often arrive with an empty MIME type). */
export function kindOf(file: { name: string; type: string }): FileKind | null {
  const type = file.type.toLowerCase();
  const ext = extension(file.name);
  for (const kind of ALL_KINDS) {
    if (type && KINDS[kind].mimes.includes(type)) return kind;
  }
  for (const kind of ALL_KINDS) {
    if (KINDS[kind].exts.includes(ext)) return kind;
  }
  return null;
}

/** "PDF", "JPG, PNG or WebP" … for friendly messages. */
export function kindsLabel(kinds: FileKind[]): string {
  const labels = kinds.map((k) => KINDS[k].label);
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} or ${labels.at(-1)}`;
}

/** Value for <input type="file" accept="…">. */
export function acceptAttribute(kinds: FileKind[]): string {
  return kinds
    .flatMap((k) => [...KINDS[k].mimes, ...KINDS[k].exts.map((e) => `.${e}`)])
    .join(",");
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < MB) return `${Math.round(bytes / 1024)} KB`;
  const mb = bytes / MB;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

export type CheckOptions = {
  accepts: FileKind[];
  /** Friendlier name for `accepts`, e.g. "Word, Excel or PowerPoint". */
  acceptsLabel?: string;
  /** Files already added, counted against maxFiles. */
  existing?: number;
  maxFiles?: number;
  maxBytes?: number;
  /** On phones, files above 25 MB get a warning (they still work). */
  isPhone?: boolean;
};

export type CheckResult<F> = {
  accepted: F[];
  /** Plain-language reasons some files were left out. */
  problems: string[];
  warnings: string[];
};

/** Split a drop/paste/browse into usable files and friendly messages (FR-2). */
export function checkFiles<
  F extends { name: string; type: string; size: number },
>(
  files: F[],
  {
    accepts,
    acceptsLabel = kindsLabel(accepts),
    existing = 0,
    maxFiles = 1,
    maxBytes = MAX_FILE_BYTES,
    isPhone = false,
  }: CheckOptions,
): CheckResult<F> {
  const accepted: F[] = [];
  const problems: string[] = [];
  const warnings: string[] = [];

  for (const file of files) {
    const kind = kindOf(file);
    if (!kind || !accepts.includes(kind)) {
      problems.push(
        `“${file.name}” can't be used here. Please choose ${acceptsLabel} files.`,
      );
    } else if (file.size > maxBytes) {
      problems.push(
        `“${file.name}” is ${formatBytes(file.size)}. The limit is ${formatBytes(maxBytes)} per file.`,
      );
    } else if (existing + accepted.length >= maxFiles) {
      problems.push(
        maxFiles === 1
          ? `This tool works on one file at a time, so “${file.name}” was left out.`
          : `You can add up to ${maxFiles} files, so “${file.name}” was left out.`,
      );
    } else {
      accepted.push(file);
      if (isPhone && file.size > PHONE_WARN_BYTES) {
        warnings.push(
          `“${file.name}” is ${formatBytes(file.size)}. Large files can be slow on a phone.`,
        );
      }
    }
  }
  return { accepted, problems, warnings };
}

/** The same name with another extension: report.docx → report.pdf
 *  (format conversions, where "-converted" would add nothing). */
export function withExtension(original: string, ext: string) {
  const dot = original.lastIndexOf(".");
  const base = (dot > 0 ? original.slice(0, dot) : original) || "file";
  return `${base}.${ext.replace(/^\./, "")}`;
}

/** `<original>-<action>.<ext>`, e.g. report.pdf + "merged" → report-merged.pdf (FR-5). */
export function outputName(original: string, action: string, ext: string) {
  const dot = original.lastIndexOf(".");
  const base = (dot > 0 ? original.slice(0, dot) : original) || "file";
  return `${base}-${action}.${ext.replace(/^\./, "")}`;
}
